import type {
	ArrayElement,
	WithRequired,
	WithUndefined,
} from '@deezer/next/services/types/utils';

export enum PeriodType {
	LAST_7_DAYS = 'LAST_7_DAYS',
	CURRENT_PITCHING_WEEK = 'CURRENT_PITCHING_WEEK',
	NEXT_PITCHING_WEEK = 'NEXT_PITCHING_WEEK',
	LAST_4_WEEKS = 'LAST_4_WEEKS',
	LAST_8_WEEKS = 'LAST_8_WEEKS',
	LAST_12_WEEKS = 'LAST_12_WEEKS',
	SINCE_START_YEAR = 'SINCE_START_YEAR',
}

export enum RangeSelectorType {
	PERIOD = 'period',
	WEEK = 'week',
	MONTH = 'month',
	YEAR = 'year',
	DAY = 'day',
	CUSTOM = 'custom',
	PRECOMPUTED_WEEK = 'weekly',
}

export type DateRangeContent = {
	startDate: Date | null;
	endDate: Date | null;
};

export type DateRangeValue = {
	[RangeSelectorType.PERIOD]: {
		period?: PeriodType;
	} & DateRangeContent;
	[RangeSelectorType.WEEK]: DateRangeContent;
	[RangeSelectorType.MONTH]: DateRangeContent;
	[RangeSelectorType.YEAR]: DateRangeContent;
	[RangeSelectorType.DAY]: DateRangeContent;
	[RangeSelectorType.CUSTOM]: DateRangeContent;
	[RangeSelectorType.PRECOMPUTED_WEEK]: DateRangeContent;
};

type DateMinMaxRangeOption = {
	minDate?: Date;
	maxDate?: Date;
};

type RequiredPickerOptions = RangeSelectorType.PRECOMPUTED_WEEK;

type ExcludedRanges<T extends RangeSelectorType[]> = Exclude<
	RangeSelectorType,
	ArrayElement<T>
>;

type _DateRangeSpecificPickerOption = {
	[RangeSelectorType.PERIOD]?: DateMinMaxRangeOption | undefined;
	[RangeSelectorType.WEEK]?: DateMinMaxRangeOption | undefined;
	[RangeSelectorType.MONTH]?: DateMinMaxRangeOption | undefined;
	[RangeSelectorType.YEAR]?: DateMinMaxRangeOption | undefined;
	[RangeSelectorType.DAY]?: DateMinMaxRangeOption | undefined;
	[RangeSelectorType.CUSTOM]?: DateMinMaxRangeOption | undefined;
	[RangeSelectorType.PRECOMPUTED_WEEK]?: DateMinMaxRangeOption & {
		weeks:
			| {
					startDate: Date;
					endDate: Date;
			  }[]
			| undefined;
	};
};

export type DateRangeSpecificPickerOption<T extends RangeSelectorType[]> =
	WithUndefined<
		RequiredPickerOptions extends ArrayElement<T>
			? WithRequired<_DateRangeSpecificPickerOption, RequiredPickerOptions>
			: _DateRangeSpecificPickerOption,
		ExcludedRanges<T>
	>;

type _DateRangePickerOptions<T extends RangeSelectorType[]> = {
	minDate: Date;
	maxDate: Date;
	periods?: PeriodType[];
	picker?: DateRangeSpecificPickerOption<T>;
};

export type DateRangePickerOptions<T extends RangeSelectorType[]> =
	RequiredPickerOptions extends ArrayElement<T>
		? WithRequired<_DateRangePickerOptions<T>, 'picker'>
		: _DateRangePickerOptions<T>;

export type DateRangePickerProps<T extends RangeSelectorType> = {
	value?: DateRangeValue[T];
	onChange: (value: DateRangeValue[T]) => void;
	options: DateRangePickerOptions<[T]>;
	isUTC?: boolean;
};
